import { ConfigProvider, DatePicker, Input, Pagination } from 'antd';
import { useMemo, useRef } from 'react';
import '../../../styles.scss';

import { SearchOutlined } from '@ant-design/icons';
import viVN from 'antd/locale/vi_VN';
import dayjs, { Dayjs } from 'dayjs';
import { ICustomer } from '../../../../../api/Customer_V2/customerApi_V2';
import { formatNumber } from '../../../../../services/mixin';
import { dataPagination } from '../../../../../until';
import { IPagination } from '../../../../Customers/CustomerGroup/Component/AddGroup/AddNewGroup';
import { FlattenedField } from '../../../../CustomersV2/indexV2';
import { IDataFilter } from '../TabInteractionHistory';
import { buildColumnsFromFields } from './Components/VoucherVersionTable';
import VoucherVersionTable from './Components/VoucherVersionTable';

const { RangePicker } = DatePicker;

interface Props {
  loadingTable: boolean;
  dataFieldsActive: FlattenedField[];
  idCustomer?: number;
  data: ICustomer[];
  pagination: IPagination;
  dataFilter: IDataFilter;

  onPaginationChange: (page: number, pageSize: number) => void;
  setDataFilter: React.Dispatch<React.SetStateAction<IDataFilter>>;
}

const TabVoucher: React.FC<Props> = ({
  idCustomer,
  loadingTable,
  dataFieldsActive,
  pagination,
  data,
  dataFilter,
  onPaginationChange,
  setDataFilter,
}) => {
  // ── Date range pending ref (commit only on dropdown close) ────────────────
  const pendingDatesRef = useRef<[Dayjs | null, Dayjs | null] | null>(null);

  // ── Build columns directly from parent's active fields ────────────────────
  const columns = useMemo(
    () => buildColumnsFromFields(idCustomer ,dataFieldsActive, dataFieldsActive.map(f => f.fieldCode)),
    [dataFieldsActive],
  );

  return (
    <div className="custom-page-css w-full flex flex-col items-center gap-3">
      {/* Filter bar */}
      <div className="custom-page-css  w-full flex gap-3">
        {/* Search */}
        <div className="custom-page-css custom-input-search w-1-3 flex items-center justify-between border-F2F4F7 bg-FCFCFD rounded-2 pl-3 overflow-hidden">
          <input
            placeholder="Tìm kiếm thông tin voucher"
            className="custom-input-css custom-page-css border-none w-full text-truncate"
            value={dataFilter.voucher.search}
            onChange={e =>
              setDataFilter(prev => ({
                ...prev,
                voucher: { ...prev.voucher, search: e.target.value },
              }))
            }
          />
          <div className="border-l-F2F4F7 h-full d-flex align-center">
            <SearchOutlined className="text-sm text-98A2B3 px-9-px" />
          </div>
        </div>
        {/* Date range */}
        <RangePicker
          popupClassName=" custom-input-date"
          placeholder={['Từ ngày', 'Đến ngày']}
          format="DD/MM/YYYY"
          allowClear
          value={
            dataFilter.voucher.fromDate && dataFilter.voucher.toDate
              ? [dayjs(dataFilter.voucher.fromDate), dayjs(dataFilter.voucher.toDate)]
              : null
          }
          onCalendarChange={dates => {
            pendingDatesRef.current = dates ?? null;
          }}
          onOpenChange={open => {
            if (!open) {
              const dates = pendingDatesRef.current;
              setDataFilter(prev => ({
                ...prev,
                voucher: {
                  ...prev.voucher,
                  fromDate: dates?.[0]?.format('YYYY/MM/DD') ?? '',
                  toDate: dates?.[1]?.format('YYYY/MM/DD') ?? '',
                },
              }));
              pendingDatesRef.current = null;
            }
          }}
        />
      </div>

      {/* Table */}
      <div style={{ width: '100%', overflowX: 'auto' }} className='voucher-table-custom'>
        <VoucherVersionTable data={data} idCustomer={idCustomer} columns={columns} loading={loadingTable} />
      </div>

      {/* Pagination */}
      <ConfigProvider locale={viVN}>
        <div className="pagination-block">
          <Pagination
            showSizeChanger
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            itemRender={(page, type, originalElement) => {
              if (type === 'page') return <span>{formatNumber(page)}</span>;
              return originalElement;
            }}
            showTotal={() =>
              `Tổng cộng ${dataPagination(
                pagination.total,
                pagination.pageSize,
                pagination.current,
              ).totalPage} trang`
            }
            className="rs-pagination"
            onChange={onPaginationChange}
          />
        </div>
      </ConfigProvider>
    </div>
  );
};

export default TabVoucher;
